import {
  ChangeDetectorRef, Component, OnInit, OnDestroy,
  Input, ViewChild, Output, EventEmitter, HostBinding,
} from '@angular/core';
import * as _ from 'lodash';
import { Observable } from 'rxjs';

import { filterPlaceholders } from 'shared/search/advanced-search/filter-placeholders';
import {
  MAKE_TYPE, YEAR_START_TYPE, YEAR_END_TYPE,
} from 'shared/search/advanced-search/filter-types';
import { BaseAdvancedSearchComponent } from 'shared/search/advanced-search/_base-advanced-search.component';
import { VehicleSearchComponent } from 'shared/search/vehicle-search/vehicle-search.component';
import { VehicleAttributesSearchComponent } from 'shared/search/vehicle-attributes-search/vehicle-attributes-search.component';
import { ReportsService } from 'shared/reports/reports.service';

import { BrandService } from 'shared/brand/brand.service';
import { FitmentsService } from 'shared/fitments/fitments.service';
import { AdvancedSearchService } from 'shared/search/advanced-search/advanced-search.service';
import {takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'report-filters',
  templateUrl: 'report-filters.component.html',
  styleUrls: ['report-filters.component.scss'],
})
export class ReportFiltersComponent extends BaseAdvancedSearchComponent implements OnInit, OnDestroy {

  collapsed: boolean = false;
  partTypes: Array<any> = [];
  selectedPartTypes: Array<any> = [];
  attributeGroups: Array<any> = [];
  showVehicleAttrs: Array<string> = [MAKE_TYPE, YEAR_START_TYPE, YEAR_END_TYPE];
  isActive: boolean = true;

  @HostBinding('class.advanced-search-host')
  hostClass: boolean = false;

  @ViewChild(VehicleSearchComponent, {static: true})
  declare vehicleSearch: VehicleSearchComponent | void;

  @ViewChild(VehicleAttributesSearchComponent, {static: false})
  declare vehicleAttributes: VehicleAttributesSearchComponent | void;

  @Output() loading: EventEmitter<boolean> = new EventEmitter();
  @Output() attrOptionsFetch: EventEmitter<boolean> = new EventEmitter();

  constructor(
    public brandService: BrandService,
    public ref: ChangeDetectorRef,
    private reportsService: ReportsService,
    private advancedSearchService: AdvancedSearchService,
    private fitmentsService: FitmentsService,
  ) {
    super(brandService, ref);
    this.filterPlaceholders = filterPlaceholders;
    this.ref.markForCheck();
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

   ngOnInit() {
     this.advancedSearchService.fetchAttributeOptions('working', this.brandId, 'partType')
       .pipe(takeWhile(() => this.isActive))
       .subscribe((res) => {
         this.partTypes = res;
         this.ref.markForCheck();
       });

     super.ngOnInit();
   }

   ngOnDestroy() {
     this.isActive = false;
   }

   /*******************************************
    * COMPONENT PUBLIC METHODS
    *******************************************/

    public getSelectedPartTypes() {
      return this.selectedPartTypes;
    }

    public resetData() {
      this.selectedPartTypes = [];
      this.partTypes = _.map(this.partTypes, type => {
        type.selected = false;
        return type;
      });
      if (this.vehicleSearch instanceof VehicleSearchComponent) {
        this.vehicleSearch.resetSelectboxes();
      }
      if (this.vehicleAttributes instanceof VehicleAttributesSearchComponent) {
        this.vehicleAttributes.resetSelectedData();
      }
      _.defer(() => {
        if (this.vehicleAttributes instanceof VehicleAttributesSearchComponent) {
          this.vehicleAttributes.selectFirstAttributeGroup();
        }
      });
      this.ref.markForCheck();
    }

   /*******************************************
    * COMPONENT HANDLERS
    *******************************************/

   onVehicleOptionsFetch(filterName: string) {
     this.loading.emit(true);
     this.fetchOptions(filterName, this.filterSearchModel)
     .subscribe((data: Array<any>) => {
       if (this.vehicleSearch instanceof VehicleSearchComponent) {
         this.vehicleSearch.setSelectData(data, filterName);
       }
       _.defer(() => this.loading.emit(false));
     });
   }

   onAttributeOptionsFetch(name: string) {
     this.loading.emit(true);
     this.fetchOptions(name, this.filterSearchModel)
     .subscribe((data: Array<any>) => {
       if (this.vehicleAttributes instanceof VehicleAttributesSearchComponent) {
         this.vehicleAttributes.setOptions(data, name);
       }
       _.defer(() => {
         this.attrOptionsFetch.emit();
         this.loading.emit(false);
       });
     });
   }

   onPartTypesOptionSelected(selected: any) {
     if (selected.selected) {
       this.selectedPartTypes.push(selected.option);
     } else {
       _.pull(this.selectedPartTypes, selected.option);
     }
   }

   onPartTypesAllOptionsSelected(selectedList: Array<any>) {
     this.selectedPartTypes = [..._.map(selectedList, 'option')];
   }

   /*******************************************
    * COMPONENT PROTECTED METHODS
    *******************************************/

   // set groupd data & add groups data from active filters
   protected setAttributeGroups(data: Array<any>): void {
     // remove Essential tab
     this.attributeGroups = _.reject(data, ['title', 'Essential']);
     this.ref.markForCheck();
     _.defer(() => {
       if (this.vehicleAttributes instanceof VehicleAttributesSearchComponent) {
         this.vehicleAttributes.setActiveFiltersData();
       }
     });
     _.delay(() => {
       if (this.vehicleAttributes instanceof VehicleAttributesSearchComponent) {
         this.vehicleAttributes.selectFirstAttributeGroup();
       }
     }, 500);
   }

   /*******************************************
    * COMPONENT PRIVATE METHODS
    *******************************************/

   private fetchOptions(filterName: string, filterSearchModel: any): Observable<any> {
     let searchModel = filterSearchModel;
     if (filterName.toLowerCase().indexOf('year') !== -1) {
       filterName = 'year';
     } else {
       // if maxYear is not selected, then set maxYear = minYear
       if (!searchModel[YEAR_END_TYPE]) {
         searchModel = _.assign(
           _.clone(filterSearchModel),
           {[YEAR_END_TYPE]: filterSearchModel[YEAR_START_TYPE]}
         );
       }
     }
     return this.fitmentsService.fetchYmmFilters(filterName, searchModel);
   }

   /*
     * Fetch attribute groups
     * Used by vehicle attributes search
   */
   private fetchAttributeGroups(): void {
     this.loading.emit(true);
     this.advancedSearchService.fetchGroupedFieldsList()
     .subscribe((data: any) => {
       this.setAttributeGroups(data);
       this.loading.emit(false);
     });
   }

}
